import { Injectable } from '@angular/core';

interface OverlayInfo {
  id: string;
  rect: DOMRect;
  component: any;
}

@Injectable({ providedIn: 'root' })
export class CommentStickyNoteOverlayService {
  private overlays: OverlayInfo[] = [];

  register(id: string, rect: DOMRect, component: any) {
    // Ensure all DOMRect properties are set
    const safeRect: DOMRect = {
      top: rect.bottom - rect.height,
      right: rect.right,
      bottom: rect.bottom,
      left: rect.right - rect.width,
      width: rect.width,
      height: rect.height,
      // DOMRect also has x/y, but not used in overlap logic
      x: rect.x ?? rect.left ?? 0,
      y: rect.y ?? rect.top ?? 0,
      toJSON: rect.toJSON ? rect.toJSON.bind(rect) : () => ({}),
    } as DOMRect;
    const idx = this.overlays.findIndex((o) => o.id === id);
    if (idx >= 0) {
      this.overlays[idx] = { id, rect: safeRect, component };
    } else {
      this.overlays.push({ id, rect: safeRect, component });
    }
  }

  unregister(id: string) {
    this.overlays = this.overlays.filter((o) => o.id !== id);
  }

  getOverlapping(id: string): OverlayInfo[] {
    const target = this.overlays.find((o) => o.id === id);
    if (!target) return [];
    return this.overlays.filter((o) => o.id !== id && this.isOverlapping(target.rect, o.rect));
  }

  /**
   * Returns all overlays involved in an overlap group, including the target overlay itself.
   */
  getOverlapGroup(id: string): OverlayInfo[] {
    const target = this.overlays.find((o) => o.id === id);
    if (!target) return [];
    // Find all overlays that overlap with the target (including the target itself)
    const group = this.overlays.filter((o) => o.id === id || this.isOverlapping(target.rect, o.rect));
    // Remove duplicates (shouldn't be any, but for safety)
    const unique = Array.from(new Set(group));
    return unique;
  }

  getAll(): OverlayInfo[] {
    return this.overlays.slice();
  }

  private isOverlapping(r1: DOMRect, r2: DOMRect): boolean {
    // Defensive: warn if any property is undefined
    const props = ['top', 'right', 'bottom', 'left', 'width', 'height'];
    for (const r of [r1, r2]) {
      for (const p of props) {
        if (typeof r[p] === 'undefined') {
          console.warn('[OverlayService] Warning: DOMRect property', p, 'is undefined in', r);
        }
      }
    }
    return r1.top < r2.bottom && r1.bottom > r2.top && r1.left < r2.right && r1.right > r2.left;
  }
}
